import axios from 'axios';
import config from '../../config';
import { apiCode, apiConfig } from '../../services';
import { clearSession, refreshSession, SessionExpiredError } from '../../services/auth';
import { setStorage, getStorage, removeStorage } from '../../helpers/storage';
import {
    setSaving,
    setPrismMenu,
    loadCustomText,
    loadHandles,
    loadTags,
    loadText,
    setFormHelp,
    setLeftAside,
    setDashboardLocked,
} from '../ui';
import { translateCompetency } from 'helpers/translations';
import posthog from 'posthog-js';

export const USER_LOGIN = 'USER_LOGIN';
export const USER_LOGOUT = 'USER_LOGOUT';
export const USER_UPDATED = 'USER_UPDATED';
export const USER_AVATAR = 'USER_AVATAR';
export const USER_LOGIN_ERROR = 'USER_LOGIN_ERROR';
export const USER_LOGIN_SUCCESS = 'USER_LOGIN_SUCCESS';
export const USER_RESET_ERROR = 'USER_RESET_ERROR';
export const USER_RESET_SUCCESS = 'USER_RESET_SUCCESS';
export const USER_LOGIN_WORKING = 'USER_LOGIN_WORKING';
export const USER_LOGIN_REDIRECTFROM = 'USER_LOGIN_SETFROM';
export const USER_LOAD_FROM_COOKIE = 'USER_LOAD_FROM_COOKIE';
export const USER_GOOGLE = 'USER_GOOGLE';
export const ALL_USERS_LOADED = 'ALL_USERS_LOADED';
export const USER_STATS_LOADED = 'USER_STATS_LOADED';
export const USER_STATS_LOADING = 'USER_STATS_LOADING';
export const USER_SETTINGS_UPDATE = 'USER_SETTINGS_UPDATE';
export const DOWNLINE_COMPETENCIES_LOADED = 'DOWNLINE_COMPETENCIES_LOADED';
export const COACHING_LOADED = 'COACHING_LOADED';
export const DOWNLINE_LOADED = 'DOWNLINE_LOADED';
export const SET_USER_PREFERENCES = 'SET_USER_PREFERENCES';

export const loginError = payload => {
    return {
        type: USER_LOGIN_ERROR,
        payload,
    };
};

export const setGoogleAuthToken = payload => {
    return {
        type: USER_GOOGLE,
        payload,
    };
};

export const resetError = payload => {
    return {
        type: USER_RESET_ERROR,
        payload,
    };
};

export const resetSuccess = payload => {
    return {
        type: USER_RESET_SUCCESS,
        payload,
    };
};

export const userUpdated = payload => {
    return {
        type: USER_UPDATED,
        payload,
    };
};

export const setUserAvatar = payload => {
    return {
        type: USER_AVATAR,
        payload,
    };
};

export const loginSuccess = payload => {
    return {
        type: USER_LOGIN_SUCCESS,
        payload,
    };
};

export const allUsersLoaded = payload => {
    return {
        type: ALL_USERS_LOADED,
        payload,
    };
};

export const userStatsLoaded = payload => {
    return {
        type: USER_STATS_LOADED,
        payload,
    };
};

export const setUserPreferences = payload => {
    return {
        type: SET_USER_PREFERENCES,
        payload,
    };
};

export const downlineLoaded = payload => {
    return {
        type: DOWNLINE_LOADED,
        payload,
    };
};

export const coachingLoaded = payload => {
    return {
        type: COACHING_LOADED,
        payload,
    };
};

export const downlineCompetenciesLoaded = payload => {
    return {
        type: DOWNLINE_COMPETENCIES_LOADED,
        payload,
    };
};

export const userStatsLoading = payload => {
    return {
        type: USER_STATS_LOADING,
        payload,
    };
};

export const updateUserCompanySettings = payload => {
    return {
        type: USER_SETTINGS_UPDATE,
        payload,
    };
};

export const loginWorking = payload => {
    return {
        type: USER_LOGIN_WORKING,
        payload,
    };
};

// Login-type calls must send credentials so the browser stores the httpOnly refresh cookie.
const loginConfig = { withCredentials: true };

export const loginRedirectFrom = payload => {
    return {
        type: USER_LOGIN_REDIRECTFROM,
        payload,
    };
};

export const postLogin = dispatch => {
    const loginPromise = new Promise((resolve, reject) => {
        try {
            e4_global.downloading = [];
        } catch {
            // Do Nothing - tests
        }

        dispatch(loadText());
        dispatch(loadCoaching());
        dispatch(loadDownline());
        dispatch(loadDownlineCompetencies());
        dispatch(loadCustomText());
        dispatch(loadHandles());
        dispatch(loadTags());
        dispatch(setAllUserPreferences());
        dispatch(setPrismMenu('Home'));
    });

    return loginPromise;
};

const setAllUserPreferences = () => {
    return (dispatch, getState) => {
        const defaultPrefs = {
            showFormHelp: true,
            leftAsideOpen: true,
            dashboardLocked: false,
        };

        const { user } = getState().user;

        const json = user.companySettings?.customText ? JSON.stringify(user.companySettings?.customText) : '';

        sessionStorage.setItem('customText', json);

        let prefObj = user?.preferences ? JSON.parse(user.preferences) : {};
        prefObj = { ...defaultPrefs, ...prefObj };
        dispatch(setFormHelp(prefObj.showFormHelp));
        dispatch(setLeftAside(prefObj.leftAsideOpen));
        dispatch(setDashboardLocked(prefObj.dashboardLocked));
    };
};

export const login = (email, password) => {
    return dispatch => {
        dispatch(loginWorking(true));
        dispatch(loginError());

        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'email-password' });

        const payload = { email, password };

        if (email === '' || password === '') {
            const error = 'You must supply both an email and password to login';
            dispatch(loginError(error));
            dispatch(loginWorking(false));
            return;
        }

        return axios
            .post(`${config.apiGateway.URL}login${apiCode()}`, payload, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('user', response.data.guid);
                setStorage('userJwt', response.data.jwt);
                removeStorage('tempUser');
                removeStorage('tempUserJwt');
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                dispatch(setSaving(false));
                const msg = err.response?.data ?? "Sorry, but we don't recognise this email / password combination.";
                posthog.capture('login-attempt-failed', { loginType: 'email-password' });
                dispatch(loginError(msg));
                dispatch(loginWorking(false));
            });
    };
};

export const loadUserFromLocalStorage = (history, overrideGuid, saml) => {
    return dispatch => {
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'local-storage' });

        const guid = overrideGuid || getStorage('user');
        const payload = { guid, saml };
        return axios
            .post(`${config.apiGateway.URL}login${apiCode()}`, payload, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('user', response.data.guid);
                setStorage('userJwt', response.data.jwt);
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                posthog.capture('login-attempt-failed', { loginType: 'local-storage' }, { transport: 'sendBeacon' });
                history?.replace?.('/login');
            });
    };
};

// Page load, and reloading the profile after a change (language, integrations): rebuild the user
// from the refresh cookie (POST auth/refresh) instead of re-posting the stored guid to /login,
// which would start a new session. If the session has gone (idle timeout, logout elsewhere) go
// to login. Any other failure (a 503, a network blip) leaves storage alone: the session may still
// be live, and the next page load can restore it. loadUserFromLocalStorage is now only the SAML
// handoff, which really is a login.
export const loadUserFromSession = history => {
    return dispatch => {
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'session' });

        return refreshSession({ force: true, redirectOnFailure: false })
            .then(data => {
                dispatch(loginSuccess(data));
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                posthog.capture('login-attempt-failed', { loginType: 'session' }, { transport: 'sendBeacon' });
                if (err instanceof SessionExpiredError) clearSession();
                history?.replace?.('/login');
            });
    };
};

export const loadUserFromAccelerate = (history, email, tenant) => {
    return dispatch => {
        const safe = encodeURIComponent(email);

        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'accelerate' });

        return axios
            .post(`${config.apiGateway.URL}accelerate-login${apiCode(true)}email=${safe}&tenant=${tenant}`, {}, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('user', response.data.guid);
                setStorage('userJwt', response.data.jwt);
                removeStorage('tempUser');
                removeStorage('tempUserJwt');
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                posthog.capture('login-attempt-failed', { loginType: 'accelerate' }, { transport: 'sendBeacon' });
                history?.replace?.('/error');
            });
    };
};

export const loadUserFromZeroGuid = (guid, history) => {
    return dispatch => {
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'zero-user' });
        return axios
            .post(`${config.apiGateway.URL}zero-login/${guid}${apiCode()}`, {}, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('tempUser', response.data.guid);
                setStorage('tempUserJwt', response.data.jwt);
                removeStorage('user');
                removeStorage('userJwt');
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                posthog.capture('login-attempt-failed', { loginType: 'zero-user' }, { transport: 'sendBeacon' });
                history?.replace?.('/error');
            });
    };
};

export const loadManagerFromZeroGuid = (guid, history) => {
    return dispatch => {
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'zero-manager' });
        return axios
            .post(`${config.apiGateway.URL}zero-manager-login/${guid}${apiCode()}`, {}, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('tempUser', response.data.guid);
                setStorage('tempUserJwt', response.data.jwt);
                removeStorage('user');
                removeStorage('userJwt');
            })
            .then(() => {
                postLogin(dispatch);
            })
            .then(() => {
                dispatch(loginWorking(false));
            })
            .catch(err => {
                posthog.capture('login-attempt-failed', { loginType: 'zero-manager' }, { transport: 'sendBeacon' });
                history?.replace?.('/error');
            });
    };
};

export const loadUserFromPartner = (guid, callback) => {
    return dispatch => {
        const payload = { guid };
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'partner' });
        return axios
            .post(`${config.apiGateway.URL}partner-portal-login${apiCode()}`, payload, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('tempUser', response.data.guid);
                setStorage('tempUserJwt', response.data.jwt);
                removeStorage('user');
                removeStorage('userJwt');
                callback?.();
            })
            .then(() => {
                postLogin(dispatch);
                dispatch(loginWorking(false));
            })
            .catch(err => {
                const msg = "Sorry, but we don't recognise this partner user.";
                posthog.capture('login-attempt-failed', { loginType: 'partner' }, { transport: 'sendBeacon' });
                dispatch(loginError(msg));
            });
    };
};

// The API's reason when it gave one: a 400 carries plain text, a 401 JSON with a message.
const spoofErrorMessage = (err, fallback) => {
    const data = err?.response?.data;
    return (typeof data === 'string' && data) || data?.message || fallback;
};

export const loadUserFromSpoofing = (email, callback, onError) => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        const payload = { email };
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'spoofing' });
        // Also switches user while already spoofing: the API takes the person really signed in from the
        // refresh cookie, so the credentials are required.
        return axios
            .post(`${config.apiGateway.URL}auth/spoof${apiCode()}`, payload, {
                ...apiConfig(guid),
                withCredentials: true,
            })
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('tempUser', response.data.guid);
                setStorage('tempUserJwt', response.data.jwt);
                removeStorage('user');
                removeStorage('userJwt');
            })
            .then(() => {
                postLogin(dispatch);
                dispatch(loginWorking(false));
                callback?.();
            })
            .catch(err => {
                const msg = spoofErrorMessage(err, "Sorry, but we don't recognise this user.");
                posthog.capture('login-attempt-failed', { loginType: 'spoofing' }, { transport: 'sendBeacon' });
                dispatch(loginError(msg));
                onError?.(msg);
            });
    };
};

// "Return to me": ends the spoof and restores the session of the person really signed in. It goes
// back in the bucket their own login used: a partner hand-off or zero login is temporary
// (tempUser), anything else is a normal login (user).
export const returnToSelf = (callback, onError) => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'spoof-end' });
        return axios
            .post(`${config.apiGateway.URL}auth/spoof/end${apiCode()}`, {}, { ...apiConfig(guid), withCredentials: true })
            .then(response => {
                const [bucket, other] = ['PARTNER', 'ZERO'].includes(response.data.authority)
                    ? ['tempUser', 'user']
                    : ['user', 'tempUser'];
                dispatch(loginSuccess(response.data));
                setStorage(bucket, response.data.guid);
                setStorage(`${bucket}Jwt`, response.data.jwt);
                removeStorage(other);
                removeStorage(`${other}Jwt`);
            })
            .then(() => {
                postLogin(dispatch);
                dispatch(loginWorking(false));
                callback?.();
            })
            .catch(err => {
                // The spoof session is left as it was, so the user can carry on or try again.
                const msg = spoofErrorMessage(err, "Sorry, we couldn't return you to your own account.");
                posthog.capture('login-attempt-failed', { loginType: 'spoof-end' }, { transport: 'sendBeacon' });
                dispatch(loginError(msg));
                onError?.(msg);
            });
    };
};

export const loadUserFromSalesforce = (accessToken, sessionId, email, url, callback) => {
    return dispatch => {
        const payload = { accessToken, sessionId, email, url };

        posthog.reset(true);
        posthog.capture('login-attempt', { loginType: 'salesforce' });

        return axios
            .post(`${config.apiGateway.URL}salesforce${apiCode()}`, payload, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('user', response.data.guid);
                setStorage('userJwt', response.data.jwt);
                removeStorage('tempUser');
                removeStorage('tempUserJwt');
                callback?.();
            })
            .then(() => {
                postLogin(dispatch);
                dispatch(loginWorking(false));
            })
            .catch(err => {
                const msg = "Sorry, but we don't recognise this salesforce user.";
                posthog.capture('login-attempt-failed', { loginType: 'salesforce' }, { transport: 'sendBeacon' });
                dispatch(loginError(msg));
            });
    };
};

export const loadUserFromAzure = (account, callback) => {
    return dispatch => {
        return axios
            .post(`${config.apiGateway.URL}azure${apiCode()}`, account, loginConfig)
            .then(response => {
                dispatch(loginSuccess(response.data));
                setStorage('user', response.data.guid);
                setStorage('userJwt', response.data.jwt);
                removeStorage('tempUser');
                removeStorage('tempUserJwt');
                callback?.();
            })
            .then(() => {
                postLogin(dispatch);
                dispatch(loginWorking(false));
            })
            .catch(err => {
                const msg = "Sorry, but we don't recognise this Azure user.";
                dispatch(loginError(msg));
            });
    };
};

export const logout = (history, authority, callback) => {
    return (dispatch, getState) => {
        const { guid, spoofing } = getState().user?.user;

        posthog.capture('logout');
        posthog.reset(true);

        if (!guid) history?.push?.('/login');

        return axios
            .post(
                `${config.apiGateway.URL}auth/logout${apiCode()}`,
                { audience: 'portal', spoofing: !!spoofing },
                { ...apiConfig(guid), withCredentials: true }
            )
            .catch(() => {})
            .finally(() => {
                clearSession();
                dispatch({
                    type: USER_LOGOUT,
                });

                //history?.push(authority === 'SAML' ? '/sso-logout' : '/login');
                history?.push?.('/login');
                callback?.();
            });
    };
};

export const loadUserStats = () => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        dispatch(userStatsLoading(true));
        return axios
            .get(`${config.apiGateway.URL}user/stats${apiCode()}`, apiConfig(guid))
            .then(response => {
                dispatch(userStatsLoaded(response.data));
                dispatch(userStatsLoading(false));
            })
            .catch(err => {
                // console.log(err);
                dispatch(userStatsLoading(false));
            });
    };
};

export const loadAllUsers = () => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        return axios
            .get(`${config.apiGateway.URL}partner/allusers${apiCode(true)}`, apiConfig(guid))
            .then(response => {
                dispatch(allUsersLoaded(response.data));
            })
            .catch(err => {
                // console.log(err);
            });
    };
};

export const loadDownline = () => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        return axios
            .get(`${config.apiGateway.URL}profile/0/downline${apiCode()}`, apiConfig(guid))
            .then(response => {
                dispatch(downlineLoaded(response.data));
            })
            .catch(err => {
                // console.log(err);
            });
    };
};

export const loadCoaching = () => {
    return (dispatch, getState) => {
        const { guid, id } = getState().user.user;
        return axios
            .get(`${config.apiGateway.URL}profile-coaching${apiCode()}`, apiConfig(guid))
            .then(response => {
                dispatch(coachingLoaded(response.data));
            })
            .catch(err => {
                // console.log(err);
            });
    };
};

export const loadDownlineCompetencies = () => {
    return (dispatch, getState) => {
        const { language_Code, guid } = getState().user.user;
        return axios
            .get(`${config.apiGateway.URL}profile-downline-competencies${apiCode()}`, apiConfig(guid))
            .then(response => {
                const mapped = response.data.map(c => translateCompetency(language_Code, c));
                dispatch(downlineCompetenciesLoaded(mapped));
            })
            .catch(err => {
                // console.log(err);
            });
    };
};

export const remind = (email, callback) => {
    return dispatch => {
        if (email === '') {
            const error = 'You must supply your email';
            dispatch(setSaving(false));
            dispatch(resetError(error));
            return;
        }

        return axios
            .post(`${config.apiGateway.URL}remind${apiCode()}`, { email }, apiConfig)
            .then(() => {
                dispatch(resetError(''));
                callback?.();
                dispatch(setSaving(false));
            })
            .catch(err => {
                dispatch(resetError(err.response?.data));
                dispatch(setSaving(false));
                // console.log(err);
            });
    };
};

export const reset = (entity, callback) => {
    return dispatch => {
        return axios
            .post(`${config.apiGateway.URL}reset${apiCode()}`, entity, apiConfig)
            .then(response => {
                if (response.data === 1) {
                    dispatch(resetError(''));
                    callback?.(true);
                } else {
                    const notStrongEnough =
                        'The password you supplied is not strong enough. Please ensure that it is at least 8 characters long and contains uppercase, lowercase and at least one number.';
                    const notExists = 'The token / email combination you supplied is not valid.';
                    const err = response.data === -1 ? notStrongEnough : notExists;
                    dispatch(resetError(err));
                }
            })
            .catch(err => {
                dispatch(resetError(err.response.data));
                callback?.(false);
            });
    };
};
